import { Book } from '@ridi/rsg';
import * as classNames from 'classnames';
import * as React from 'react';
import { AuthorOrdered, BookDto } from "app/types/scheme";
import styled from "@emotion/styled";
import { slateGray60 } from "@ridi/colors";
import BookBadgeRenderer from "app/components/badge/BookBadgeRenderer";
import FreeBookRenderer from "app/components/badge/FreeBookRenderer";
import SetBookRenderer from "app/components/badge/SetBookRenderer";
import { get } from 'lodash'

export interface Props {
  className?: string;
  thumbnailSize?: 50 | 60 | 70 | 80 | 90 | 100 | 110 | 120 | 150 | 180 | 200
  book: BookDto;
  genre: string;
}

const AuthorsWrapper = styled.span`
  font-size: 12px;
  line-height: 1.3em;
  color: ${slateGray60};
  margin-bottom: 2px;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  word-wrap: break-word;
  white-space: normal;
  word-break: keep-all;

  @media (-ms-high-contrast: none), (-ms-high-contrast: active) {
    white-space: nowrap;
  }
`;

function AuthorAnchor(props: { author: AuthorOrdered }) {
  const { id, name } = props.author;
  return (
    <a
      href={
        id
          ? `/author/${id}`
          : `/search?q=${encodeURIComponent(name)}`
      }
    >
      {name}
    </a>
  );
}

function Authors(props: { authors: AuthorOrdered[] }) {
  const { authors } = props;
  const len = authors.length;
  if (len === 0) {
    return null;
  }
  if (len === 1) {
    return <AuthorAnchor author={authors[0]} />;
  }
  if (len === 2) {
    return (
      <>
        <AuthorAnchor author={authors[0]} />
        {', '}
        <AuthorAnchor author={authors[1]} />
      </>
    );
  }

  // len > 2
  return (
    <>
      <AuthorAnchor key="0" author={authors[0]} />
      {', '}
      <AuthorAnchor key="1" author={authors[1]} />
      {` 외 ${len - 2}명`}
    </>
  );
}

export const BadgeContainer = styled.div`
  position: absolute;
  display: block;
  top: -7px;
  left: -7px;
  z-index: 2;
`;


export const getMaxDiscountPercentage = (book: any) => {
  if (!book) {
    return 0;
  }
  const singleBuyDiscountPercentage = get(book, 'price_info.buy.discount_percentage', 0);
  const singleRentDiscountPercentage = get(book, 'price_info.rent.discount_percentage', 0);
  const seriesBuyDiscountPercentage = get(book, 'series.property.is_serial', false) ? get(book, 'series.price_info.buy.discount_percentage', 0) : 0;
  const seriesRentDiscountPercentage = get(book, 'series.property.is_serial', false) ? get(book, 'series.price_info.rent.discount_percentage', 0) : 0;

  const excludeMax = [
    singleBuyDiscountPercentage,
    seriesBuyDiscountPercentage,
    singleRentDiscountPercentage,
    seriesRentDiscountPercentage,
  ].filter((dp) => dp < 100);

  const maxValue = Math.round(Math.max(...excludeMax));
  if (maxValue >= 10) {
    return maxValue;
  }
  return 0;
};


export function PortraitBook(props: Props) {
  const {
    className,
    thumbnailSize,
    book,
    genre
  } = props

  // RSG 코드 내에 썸네일 링크가
  // /v2/Detail?id={bId} 로 박힌 상태. 일단 유지하고 페이지 전체를 반응형으로 변경할 때 RSG 사용하지 않기
  const mergedAuthors = book && book.authors && book.authors.filter(
    (author: AuthorOrdered) => ['author', 'comic_author', 'story_writer', 'illustrator', 'original_author', 'editor'].includes(author.role),
  );
  const singlePriceInfo = book.price_info;
  const seriesPriceInfo = get(book, 'series.price_info', null);
  const isSingleRentable = !!singlePriceInfo.rent;
  const isSeriesRentable = !!get(seriesPriceInfo, 'rent', null) && get(book, 'book.series.property.is_serial', false);
  return (
    <Book
      className={classNames('RSGBook-preset-portrait', className)}
      style={{ width: `${thumbnailSize}px` }}
      // @ridi/rsg book dto 와 맞지 않음
      dto={book as any}
    >
      {({ Thumbnail, Metadata }) => {
        return (
          <>
            <Thumbnail.wrapper thumbnailSize={thumbnailSize}>
              <Thumbnail.coverImage />
              <Thumbnail.adultOnlyBadge />
              <BadgeContainer>
                <BookBadgeRenderer
                  isRentable={
                    isSingleRentable
                      || isSeriesRentable
                    && ['general', 'romance', 'bl'].includes(genre)
                  }
                  isWaitFree={get(book, 'series.property.is_wait_free', false)}
                  discountPercentage={getMaxDiscountPercentage(book)}
                />
              </BadgeContainer>
              <FreeBookRenderer
                freeBookCount={
                  get(seriesPriceInfo, 'rent.free_book_count', 0)
                  || get(seriesPriceInfo ,'buy.free_book_count', 0)
                  || 0
                }
                unit={get(book, 'series.property.unit', '권')}
              />
              <SetBookRenderer
                setBookCount={get(book, 'setbook.member_books_count', 0)}
              />
            </Thumbnail.wrapper>
            <Metadata.wrapper>
              <a href={`/books/${props.book.id}`} className="RSGBookMetadata_Title">
                {get(props.book, 'series.property.title', props.book.title.main)}
              </a>
              <AuthorsWrapper>
                <Authors authors={Array.isArray(mergedAuthors) ? mergedAuthors : []} />
              </AuthorsWrapper>
            </Metadata.wrapper>
          </>
        );
      }}
    </Book>
  );
};




// WEBPACK FOOTER //
// ./components/book/PortraitBook.tsx